<template>
  <SheetModal
    data-theme="light"
    aria-label="Manage your courts"
    :aria-busy="isRequesting"
    @close="handleClose"
  >
    <div
      class="ManageCourtsModalView__container"
    >
      <SheetHeader
        title="Manage your courts"
        cancelLabel="Close"
        excludeSubmitButton
        @cancel="handleClose"
      />
      <div class="ManageCourtsModalView__content">
        <Transition>
          <ul
            v-if="!isRequesting && courts.length > 0"
            class="ManageCourtsModalView__courtList"
            aria-label="Courts in your crews"
          >
            <li
              v-for="court in courts"
              :key="court.id"
              class="ManageCourtsModalView__courtItem"
              :aria-busy="savingCourtIds.has(court.id)"
              :data-enabled="courtIds.has(court.id)"
            >
              <div class="ManageCourtsModalView__courtIconContainer">
                <Basketball class="ManageCourtsModalView__courtIcon" />
              </div>
              <div class="ManageCourtsModalView__courtContent">
                <span class="ManageCourtsModalView__courtName">{{ court.name }}</span>
                <span
                  v-if="court.address"
                  class="ManageCourtsModalView__courtAddress"
                >{{ court.address }}</span>
              </div>
              <div class="ManageCourtsModalView__courtButtonContainer">
                <IconButton
                  shape="circle"
                  size="smedium"
                  iconSize="smedium"
                  role="switch"
                  :aria-checked="courtIds.has(court.id)"
                  :aria-label="court.name"
                  :variant="courtIds.has(court.id) ? 'secondary' : 'primary'"
                  :iconName="courtIds.has(court.id) ? 'minus' : 'plus'"
                  :disabled="savingCourtIds.has(court.id)"
                  @click="handleCourtButtonClick(court.id)"
                />
              </div>
            </li>
          </ul>
        </Transition>
      </div>
    </div>
  </SheetModal>
</template>

<script setup lang="ts">
import Basketball from "@/components/Basketball.vue";
import IconButton from "@/components/IconButton.vue";
import SheetHeader from "@/components/SheetHeader.vue";
import SheetModal from "@/components/SheetModal.vue";
import { supabaseClient } from "@/lib/database/supabase";
import haptic from "@/lib/utils/platform/haptic";
import useCommissionerCourtStore from "@/stores/useCommissionerCourtStore";
import useCurrentPlayerStore from "@/stores/useCurrentPlayerStore";
import { Court } from "@shared/lib/database/query.types";
import { storeToRefs } from "pinia";
import { onBeforeMount, ref } from "vue";

const emit = defineEmits<{
  close: [],
}>();

const {
  commissionerCrews,
} = storeToRefs(useCurrentPlayerStore());

const {
  fetchCrewMemberships,
} = useCurrentPlayerStore();

const {
  commissionerCourts,
} = storeToRefs(useCommissionerCourtStore());

const {
  fetchCommissionerCourts,
  updateCommissionerCourts,
} = useCommissionerCourtStore();

const isRequesting = ref(true);
const courtIds = ref<Set<string>>(new Set());
const courts = ref<Array<Court>>([]);
const savingCourtIds = ref(new Set<string>());
updateInitialCourts();

onBeforeMount(async () => {
  try {
    await Promise.all([fetchCommissionerCourts(), fetchCrewMemberships()]);
    updateInitialCourts();
  } catch (error) {
    alert((error as Error).message);
  } finally {
    isRequesting.value = false;
  }
});

function updateInitialCourts () {
  courtIds.value = new Set(commissionerCourts.value.map((court) => court.id));
  courts.value = [...commissionerCourts.value];
}

async function handleCourtButtonClick (courtId: string) {
  haptic();

  savingCourtIds.value.add(courtId);

  // TODO: handle someone being a commissioner of multiple crews
  const {
    error,
  } = await supabaseClient.rpc("sync_court_crews", {
    court_id: courtId,
    crew_ids: courtIds.value.has(courtId) ? [] : commissionerCrews.value.map((crew) => crew.id),
  });

  if (error) {
    alert((error as Error).message);
  } else {
    if (courtIds.value.has(courtId)) {
      courtIds.value.delete(courtId);
    } else {
      courtIds.value.add(courtId);
    }

    updateCommissionerCourts(courts.value.filter((court) => courtIds.value.has(court.id)));
  }

  savingCourtIds.value.delete(courtId);
}

function handleClose () {
  emit("close");
}
</script>

<style lang="scss" scoped>
.ManageCourtsModalView__container {
  display: grid;
  grid-template-rows: min-content auto;
  height: 100%;
}

.ManageCourtsModalView__content {
  position: relative;
  min-height: 0;
  background-color: var(--themeColorWhite);
}

.ManageCourtsModalView__courtList {
  position: absolute;
  inset: 0;

  display: grid;
  gap: 1px;
  align-content: start;
  padding-bottom: var(--safeAreaInsetBottom);
  overflow-y: auto;

  transition: opacity 350ms;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.ManageCourtsModalView__courtItem {
  display: block;
  display: grid;
  grid-template-columns: min-content auto min-content;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 16px;

  outline: 1px solid var(--themeColorNeutral100);
  background-color: var(--themeColorWhite);

  text-align: left;
}

.ManageCourtsModalView__courtIconContainer {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;

  border: solid 3px var(--themeColorPrimary);
  border-radius: 50%;

  transition: 350ms;
  transition-property: color, filter, opacity;

  .ManageCourtsModalView__courtItem[aria-busy="true"] &,
  .ManageCourtsModalView__courtItem[data-enabled="false"] & {
    color: var(--themeDarkTransparent300);
    opacity: 0.5;
    filter: grayscale(100%);
  }
}

.ManageCourtsModalView__courtIcon {
  width: 36px;
  height: 36px;
}

.ManageCourtsModalView__courtContent {
  position: relative;
  top: var(--fontSafariOffset);

  display: grid;
  gap: 8px;

  transition: opacity 350ms;

  .ManageCourtsModalView__courtItem[aria-busy="true"] &,
  .ManageCourtsModalView__courtItem[data-enabled="false"] & {
    opacity: 0.5;
  }
}

.ManageCourtsModalView__courtName {
  overflow: hidden;

  color: var(--themeColorInputForeground);

  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-overflow: ellipsis;
  white-space: nowrap;

  .ManageCourtsModalView__courtItem[data-enabled="false"] & {
    text-decoration: line-through;
    text-decoration-thickness: 1.5px;
  }
}

.ManageCourtsModalView__courtAddress {
  overflow: hidden;

  color: var(--themeColorNeutral500);

  font-size: 14px;
  font-stretch: var(--fontWidthExtended);
  text-overflow: ellipsis;
  white-space: nowrap;

  .ManageCourtsModalView__courtItem[data-enabled="false"] & {
    text-decoration: line-through;
    text-decoration-thickness: 1.5px;
  }
}
</style>
